import { useQuery } from '@tanstack/react-query';
import { getMenu } from '@/api/menu';
import { useAuth } from '@/auth/useAuth';
import { buildFallbackTree } from '@/menu/fallback';
import type { MenuNode } from '@/types/menu';

/** Query key shared by the sidebar and the Menu config screen (which invalidates it). */
export const MENU_QUERY_KEY = ['menu'] as const;

export interface MenuTreeState {
  nodes: MenuNode[];
  isLoading: boolean;
  /** GET /api/menu failed and the built-in menu (src/menu/fallback.ts) is showing. */
  usingFallback: boolean;
}

/**
 * The caller's sidebar tree. While it loads, `nodes` is empty; if GET /api/menu
 * fails — migration 131 not yet run, or the Function App / SQL unreachable —
 * the built-in copy of the seeded menu is served, filtered by the caller's roles
 * with the server's rule, so navigation never disappears.
 */
export function useMenuTree(): MenuTreeState {
  const { roles } = useAuth();
  const query = useQuery({
    queryKey: MENU_QUERY_KEY,
    queryFn: ({ signal }) => getMenu(signal),
    staleTime: 5 * 60_000,
    // One retry, not three: every failed attempt is a moment with no menu.
    retry: 1,
  });
  const usingFallback = query.isError;
  return {
    nodes: query.data ?? (usingFallback ? buildFallbackTree(roles ?? []) : []),
    isLoading: query.isLoading,
    usingFallback,
  };
}
